import { SaveOutlined, DownloadOutlined } from "@ant-design/icons";
import {
  Button,
  Col,
  DatePicker,
  Flex,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Upload,
} from "antd";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { createUseStyles } from "react-jss";
import useSystemNoticeApi from "../../apiHooks/useSystemNoticeApi";
import { _STYLES } from "../../common/utils/customStyles";
import { useNotificationContext } from "../../core/contexts/NotificationContextProvider";
import { InboxOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { downloadFiles, sanitizeHTML } from "../../common/utils/common";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { errorColor, grey4 } from "../../theme";

const EDITOR_MAX_CHARS = 300;

export const POST_TYPES = {
  text: "Text",
  image: "Image",
};

const POST_TYPE_VALS = [
  {
    label: "Text",
    value: POST_TYPES.text,
  },
  {
    label: "Image",
    value: POST_TYPES.image,
  },
];

const POST_PRIORITIES = [
  {
    label: "General",
    value: "General",
  },
  {
    label: "High",
    value: "High",
  },
  {
    label: "Medium",
    value: "Medium",
  },
  {
    label: "Low",
    value: "Low",
  },
];

const POST_HEADERS = [
  {
    label: "Import",
    value: "Import",
  },
  {
    label: "Export",
    value: "Export",
  },
  {
    label: "Other",
    value: "Other",
  },
];

const { mt1, textSm3, uploadLogo, w100, downloadBtn, mb2, resetStyles } =
  _STYLES;
const useStyles = createUseStyles({
  mt1,
  textSm3,
  uploadLogo,
  w100,
  downloadBtn,
  mb2,
  resetStyles,
  "& .ql-toolbar.ql-snow": {
    borderRadius: "8px 8px 0 0 !important",
    border: "1px solid #dde8f4 !important",
    background: "#f8fafd !important",
  },
  "& .ql-container.ql-snow": {
    borderRadius: "0 0 8px 8px !important",
    border: "1px solid #dde8f4 !important",
    borderTop: "none !important",
  },
  quillWrap: {
    "& .ql-toolbar.ql-snow": {
      borderRadius: "8px 8px 0 0",
      border: "1px solid #dde8f4",
      background: "#f8fafd",
    },
    "& .ql-container.ql-snow": {
      borderRadius: "0 0 8px 8px",
      border: "1px solid #dde8f4",
      borderTop: "none",
    },
  },
  datePicker: {
    borderRadius: "8px !important",
  },
  submitBtn: {
    height: "44px !important",
    padding: "0 28px !important",
    fontSize: "0.9rem !important",
    borderRadius: "8px !important",
  },
});

const SystemNoticeModal = forwardRef(
  ({ isModelOpen, toggleModal, cbOnSave, isDisabled }, ref) => {
    const classes = useStyles();

    const [selectedRecord, setSelectedRecord] = useState(null);
    const [charCount, setCharCount] = useState(0);
    const [editorValue, setEditorValue] = useState("");

    const quillRef = useRef(null);

    const { notify } = useNotificationContext();

    const {
      createSystemNoticeApi: { create: createSystemNotice, isLoading },
      updateSystemNoticeApi: {
        execute: updateSystemNotice,
        isLoading: isUpdating,
      },
    } = useSystemNoticeApi();

    useImperativeHandle(ref, () => ({
      async getSystemNoticeRecord(data) {
        setSelectedRecord(data);
      },
    }));

    const [form] = Form.useForm();

    const postImageFile = Form.useWatch("PostImageFile", form);
    const postType = Form.useWatch("PostType", form);

    const isUpdate = !!selectedRecord;

    const handleSubmit = useCallback(
      async (data) => {
        if (data.PostType === POST_TYPES.text) {
          const quill = quillRef.current?.getEditor();

          if (quill?.getText().length > EDITOR_MAX_CHARS) {
            notify({
              type: "error",
              message: "Message body must not exceed 300 characters",
            });
            return;
          }
        }

        if (isUpdate) {
          data.Id = selectedRecord.id;
        }
        data = {
          ...data,
          ExpiryDate: dayjs(data.ExpiryDate).format("YYYY-MM-DD"),
          MessageBody: sanitizeHTML(data.MessageBody),
        };

        const formData = new FormData();

        Object.keys(data).forEach((key) => {
          if (key === "PostImageFile") {
            if (Array.isArray(data[key]?.fileList)) {
              data[key].fileList.forEach((f) => {
                if (f.originFileObj) {
                  formData.append("PostImageFile", f.originFileObj);
                }
              });
            }
          } else {
            formData.append(key, data[key]);
          }
        });

        const res = await (isUpdate
          ? updateSystemNotice(formData)
          : createSystemNotice(formData));
        if (res) {
          toggleModal();
          notify({
            message: `Notice ${isUpdate ? "updated" : "added"} successfully`,
          });
          cbOnSave?.();
        }
      },
      [
        cbOnSave,
        createSystemNotice,
        isUpdate,
        notify,
        selectedRecord?.id,
        toggleModal,
        updateSystemNotice,
      ]
    );

    const handleUpload = useCallback(
      (file) => {
        if (postImageFile?.fileList?.length) {
          notify({
            type: "error",
            message: "A file has already been selected",
          });
          return Upload.LIST_IGNORE;
        }
        return false;
      },
      [postImageFile?.fileList?.length, notify]
    );

    const handleEditorChange = (content, delta, source, editor) => {
      const length = editor.getText().trim().length;
      setCharCount(length);
      setEditorValue(content);
      form.setFieldValue("MessageBody", length > 0 ? content : "");
    };

    //disabled for edit with insufficient permissions
    const isReadOnly = isUpdate && isDisabled;

    useEffect(() => {
      if (!isModelOpen) {
        form.resetFields();
        setSelectedRecord(null);
        setEditorValue("");
        setCharCount(0);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isModelOpen]);

    useEffect(() => {
      if (selectedRecord) {
        const body = sanitizeHTML(selectedRecord.messageBody);
        setEditorValue(body);
        form.setFieldsValue({
          Priority: selectedRecord.priority,
          PostingHeader: selectedRecord.postingHeader,
          PostType: selectedRecord.postType,
          Subject: selectedRecord.subject,
          MessageBody: body,
          ExpiryDate: selectedRecord.expiryDate
            ? dayjs(selectedRecord.expiryDate)
            : null,
        });
      } else {
        form.setFieldValue("PostType", POST_TYPES.text);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedRecord]);

    return (
      <>
        <Modal
          title={isReadOnly ? "View Notice" : "Configure Notice"}
          open={isModelOpen}
          onCancel={() => toggleModal()}
          footer={null}
          width={720}
          centered
        >
          <Row className={classes.mt1}>
            <Col xs={24}>
              <Form
                name="systemNotice"
                initialValues={{ remember: false }}
                onFinish={handleSubmit}
                autoComplete="off"
                layout="vertical"
                form={form}
              >
                <Row gutter={[32, 0]}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      label="Priority"
                      name="Priority"
                      rules={[{ required: true, message: "Please select a Priority" }]}
                    >
                      <Select disabled={isReadOnly} options={POST_PRIORITIES} placeholder="Select a Priority" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      label="Posting Header"
                      name="PostingHeader"
                      rules={[{ required: true, message: "Please select a Posting Header" }]}
                    >
                      <Select disabled={isReadOnly} options={POST_HEADERS} placeholder="Select a Posting Header" />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item
                  label="Post Type"
                  name="PostType"
                  rules={[
                    {
                      required: true,
                      message: "Please select a Post Type",
                    },
                  ]}
                >
                  <Select
                    disabled={isReadOnly}
                    options={POST_TYPE_VALS}
                    placeholder="Select a Post Type"
                  />
                </Form.Item>
                <Form.Item
                  label="Subject"
                  name="Subject"
                  rules={[
                    {
                      required: true,
                      message: "Please enter a Subject",
                    },
                  ]}
                >
                  <Input disabled={isReadOnly} allowClear />
                </Form.Item>

                {postType === POST_TYPES.image ? (
                  <Flex className={classes.mb2} vertical gap={8}>
                    <Form.Item
                      style={{ marginBottom: 8 }}
                      label="Post Image"
                      name="PostImageFile"
                      valuePropName="PostImageFile"
                      rules={[
                        {
                          required: !isUpdate,
                          message: "Please upload an Image",
                        },
                      ]}
                      wra
                    >
                      <Upload.Dragger
                        disabled={isReadOnly}
                        beforeUpload={handleUpload}
                        onRemove={() => {
                          form.setFieldValue("PostImageFile", undefined);
                        }}
                      >
                        <Flex vertical align="center" justify="center" gap={8}>
                          <InboxOutlined className={classes.uploadLogo} />
                          <p className={classes.textSm3}>
                            Click or drag file to this area to upload
                          </p>
                        </Flex>
                      </Upload.Dragger>
                    </Form.Item>
                    {isUpdate && (
                      <Button
                        type="primary"
                        className={classes.downloadBtn}
                        icon={<DownloadOutlined />}
                        style={{ width: "max-content" }}
                        onClick={() => downloadFiles(selectedRecord.postImage)}
                      >
                        View Uploaded Image
                      </Button>
                    )}
                  </Flex>
                ) : (
                  <>
                    {isReadOnly ? (
                      <Flex className={classes.mb2} vertical gap={8}>
                        <label className={classes.textSm3}>Message Body</label>
                        <div
                          className={classes.resetStyles}
                          dangerouslySetInnerHTML={{
                            __html: sanitizeHTML(selectedRecord.messageBody),
                          }}
                        />
                      </Flex>
                    ) : (
                      <Flex className={classes.mb2} vertical gap={4}>
                        <Form.Item
                          style={{ marginBottom: 0 }}
                          label="Message Body"
                          name="MessageBody"
                          rules={[
                            {
                              required: true,
                              message: "Please enter Message Body",
                            },
                          ]}
                        >
                              <div className={classes.quillWrap}>
                            <ReactQuill
                              ref={quillRef}
                              value={editorValue}
                              disabled={isReadOnly}
                              theme="snow"
                              onChange={handleEditorChange}
                            />
                          </div>
                        </Form.Item>
                        <p
                          style={{
                            color:
                              charCount > EDITOR_MAX_CHARS ? errorColor : grey4,
                          }}
                          className={classes.textSm3}
                        >
                          {charCount} / {EDITOR_MAX_CHARS}
                        </p>
                      </Flex>
                    )}
                  </>
                )}

                <Form.Item
                  label="Expiry Date"
                  name="ExpiryDate"
                  rules={[
                    {
                      required: true,
                      message: "Please select an Expiry Date",
                    },
                  ]}
                >
                  <DatePicker disabled={isReadOnly} className={`${classes.w100} ${classes.datePicker}`} />
                </Form.Item>

                {!isReadOnly && (
                  <Flex justify="flex-end">
                    <Button
                      icon={<SaveOutlined />}
                      loading={isLoading || isUpdating}
                      type="primary"
                      htmlType="submit"
                      className={classes.submitBtn}
                    >
                      {isUpdate ? "Update" : "Create Notice"}
                    </Button>
                  </Flex>
                )}
              </Form>
            </Col>
          </Row>
        </Modal>
      </>
    );
  }
);

export default SystemNoticeModal;
